import React, { useState, useEffect, useMemo, useRef } from 'react';
import ProjectSelect from './ProjectSelect';
import AgentSelect from './AgentSelect';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Avatar,
  Autocomplete,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { t } from '../localization';
import { getProjectStatuses } from '../services/statusService';
import { customFieldsApi } from '../services/api';

const TicketForm = ({ open, onClose, onSubmit, ticket, user, projects = [], projectId = '', authors = [], assignees = [] }) => {
  // Определяем, находится ли тикет в финальном статусе
  const isTicketInFinalStatus = ticket?.statusObj?.isFinal === true;
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    priority: 'normal', 
    type: 'unknown', 
    statusId: '', 
    plannedEndDate: '', 
    projectId: projectId || '',
    assignedToId: '',
    createdById: user?.id || ''
  });
  const [attachments, setAttachments] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [customInputs, setCustomInputs] = useState({});
  const [loadingFields, setLoadingFields] = useState(false);
  const defaultFilter = useMemo(() => createFilterOptions(), []);
  const originalProjectId = useMemo(() => ticket?.project?.id || ticket?.projectId || '', [ticket]);
  const prevProjectIdRef = useRef(formData.projectId);
  const selectedAuthor = useMemo(
    () => authors.find(a => String(a.id) === String(formData.createdById)) || null,
    [authors, formData.createdById]
  );

  // Загрузка статусов проекта при изменении projectId
  useEffect(() => {
    const fetchStatuses = async (pid) => {
      if (!pid) return;
      
      setLoadingStatuses(true);
      try {
        const statuses = await getProjectStatuses(pid);
        setProjectStatuses(statuses);
        
        const sameProject = !ticket || String(pid) === String(originalProjectId);
        const initialStatus = statuses.find(status => status.isInitial) || statuses[0];

        setFormData(prev => {
          if (prev.statusId) return prev;
          if (sameProject && ticket?.statusObj?.id) {
            return { ...prev, statusId: ticket.statusObj.id };
          }
          if (initialStatus) {
            return { ...prev, statusId: initialStatus.id };
          }
          return prev;
        });
      } catch (error) {
        console.error('Failed to fetch statuses:', error);
      } finally {
        setLoadingStatuses(false);
      }
    };

    const currentProjectId = formData.projectId;
    if (currentProjectId) {
      fetchStatuses(currentProjectId);
    }
  }, [formData.projectId, ticket, originalProjectId]);

  // Загрузка кастомных полей проекта
  useEffect(() => {
    const fetchFields = async (pid) => {
      if (!pid) return;
      setLoadingFields(true);
      try {
        const res = await customFieldsApi.getProjectFields(pid);
        setCustomFields((res.data || []).filter(f => f.isActive));
        const sameProject = !ticket || String(pid) === String(originalProjectId);
        // Инициализация значений при редактировании
        if (ticket?.customFieldValues?.length && sameProject) {
          const values = {};
          ticket.customFieldValues.forEach(v => {
            if (v.field?.id) values[v.field.id] = v.value;
          });
          setCustomValues(values);
        } else {
          setCustomValues({});
        }
        setCustomInputs({});
      } catch (e) {
        console.error('Failed to fetch custom fields:', e);
      } finally {
        setLoadingFields(false);
      }
    };
    if (formData.projectId) {
      fetchFields(formData.projectId);
    }
  }, [formData.projectId, ticket, originalProjectId]);

  useEffect(() => {
    // Если это создание нового тикета и есть только один проект - используем его
    const defaultProjectId = !ticket && projects.length === 1 
      ? projects[0].id 
      : ticket?.project?.id || ticket?.projectId || projectId || '';
    
    setFormData({
      title: ticket?.title || '',
      description: ticket?.description || '',
      priority: ticket?.priority || 'normal',
      type: ticket?.type || 'unknown',
      statusId: ticket?.statusObj?.id || '',
      plannedEndDate: ticket?.plannedEndDate ? ticket.plannedEndDate.slice(0, 10) : '',
      projectId: defaultProjectId,
      assignedToId: ticket?.assignedToId || '',
      createdById: ticket?.createdById || ticket?.createdBy?.id || user?.id || '',
    });
    setAttachments([]);
  }, [ticket, projectId, projects]);

  // Сброс формы при открытии для создания нового тикета
  useEffect(() => {
    if (open && !ticket) {
      // Сброс всех полей формы при создании нового тикета
      setFormData({
        title: '',
        description: '',
        priority: 'normal',
        type: 'unknown',
        statusId: '',
        plannedEndDate: '',
        projectId: projectId || (projects.length === 1 ? projects[0].id : ''),
        assignedToId: '',
        createdById: user?.id || ''
      });
      setAttachments([]);
      setCustomValues({});
      setCustomInputs({});
      
      // Для обычных пользователей автоматически устанавливаем статус после загрузки статусов
      // Статус будет установлен в useEffect для установки статуса
    }
  }, [open, ticket, projectId, projects]);

  // Установка статуса при создании нового тикета после загрузки статусов
  useEffect(() => {
    if (!ticket && projectStatuses.length > 0 && !formData.statusId) {
      // Находим первый статус (обычно "Новый" или начальный статус)
      const initialStatus = projectStatuses.find(status => status.isInitial) || projectStatuses[0];
      if (initialStatus) {
        setFormData(prev => ({
          ...prev,
          statusId: initialStatus.id
        }));
      }
    }
  }, [projectStatuses, ticket, formData.statusId]);

  // Сброс формы при закрытии диалога
  useEffect(() => {
    if (!open) {
      // Сброс всех полей формы при закрытии
      setFormData({
        title: '',
        description: '',
        priority: 'normal',
        type: 'unknown',
        statusId: '',
        plannedEndDate: '',
        projectId: '',
        assignedToId: '',
        createdById: user?.id || ''
      });
      setAttachments([]);
      setCustomValues({});
      setCustomInputs({});
    }
  }, [open]);

  useEffect(() => {
    const prevProjectId = prevProjectIdRef.current;
    if (prevProjectId === formData.projectId) return;
    prevProjectIdRef.current = formData.projectId;

    if (!ticket) return;
    if (!formData.projectId) return;
    if (String(formData.projectId) === String(originalProjectId)) return;

    setFormData(prev => ({ ...prev, statusId: '' }));
    setCustomValues({});
    setCustomInputs({});
  }, [formData.projectId, ticket, originalProjectId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Предотвращаем отправку формы, если тикет в финальном статусе
    if (isTicketInFinalStatus) {
      // Просто закрываем форму без алерта
      onClose();
      return;
    }
    
    let data;
    if (attachments.length) {
      data = new FormData();
      
      // Добавляем обычные поля формы
      Object.entries(formData).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          data.append(key, val);
        }
      });
      
      // Добавляем файлы с дополнительной информацией для сохранения кириллических имён
      Array.from(attachments).forEach((file, index) => {
        // Добавляем сам файл
        data.append('attachments', file);
        
        // Добавляем оригинальное имя файла в отдельное поле
        data.append(`filename-${index}`, file.name);
        
        // Добавляем закодированное имя в base64 для надежного сохранения кириллицы
        const encodedName = btoa(unescape(encodeURIComponent(file.name)));
        data.append(`filename-encoded-${index}`, encodedName);
      });
      
      // Добавляем количество файлов для правильной обработки на сервере
      data.append('fileCount', attachments.length);
      // Кастомные поля
      if (customFields.length) {
        const payload = customFields.map(f => ({ fieldId: f.id, value: customValues[f.id] }));
        data.append('customFields', JSON.stringify(payload));
      }
    } else {
      data = { ...formData };
      if (customFields.length) {
        data.customFields = customFields.map(f => ({ fieldId: f.id, value: customValues[f.id] }));
      }
    }
    
    // projectId всегда включаем при создании
    if (!ticket && projectId) {
      if (data instanceof FormData) {
        data.set('projectId', projectId);
      } else {
        data.projectId = projectId;
      }
    }
    
    // Для обычных пользователей при создании тикета автоматически устанавливаем начальный статус
    if (!ticket && !(user?.role === 'admin' || user?.role === 'manager') && !data.statusId) {
      const initialStatus = projectStatuses.find(status => status.isInitial) || projectStatuses[0];
      if (initialStatus) {
        if (data instanceof FormData) {
          data.set('statusId', initialStatus.id);
        } else {
          data.statusId = initialStatus.id;
        }
      }
    }
    
    // Пользователи без прав админа не могут менять автора, поэтому не отправляем это поле
    if (user?.role !== 'admin') {
      if (data instanceof FormData) {
        data.delete('createdById');
      } else {
        delete data.createdById;
      }
    }
    
    onSubmit(data);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {ticket ? t('edit') + ' ' + t('ticket') : t('create') + ' ' + t('ticket')}
        {isTicketInFinalStatus && ' (только просмотр)'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {/* Выбор проекта всегда, если есть проекты */}
          {projects.length > 0 && (
            <ProjectSelect
              value={formData.projectId}
              onChange={val => setFormData({ ...formData, projectId: val })}
              projects={projects}
              disabled={projects.length === 1 || (ticket && user?.role !== 'admin')}
              showAllOption={false}
            />
          )}
          {/* Кастомные поля */}
          {customFields.map(field => {
            const value = customValues[field.id];
            const setValue = (val) => setCustomValues(prev => ({ ...prev, [field.id]: val }));
            const handleInputChange = (_, newInputValue, reason) => {
              if (reason === 'reset') return;
              setCustomInputs(prev => ({ ...prev, [field.id]: newInputValue || '' }));
            };
            const inputValue = customInputs[field.id] || '';
            if (field.type === 'TEXT') {
              return (
                <TextField
                  key={field.id}
                  margin="dense"
                  label={field.label}
                  fullWidth
                  value={value || ''}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={isTicketInFinalStatus}
                  helperText={field.helpText || ''}
                  required={field.required}
                />
              );
            }
            if (field.type === 'TEXTAREA') {
              return (
                <TextField
                  key={field.id}
                  margin="dense"
                  label={field.label}
                  fullWidth
                  multiline
                  rows={3}
                  value={value || ''}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={isTicketInFinalStatus}
                  helperText={field.helpText || ''}
                  required={field.required}
                />
              );
            }
            if (field.type === 'DATETIME') {
              return (
                <Box key={field.id} sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    margin="dense"
                    label={field.label}
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={value || ''}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={isTicketInFinalStatus}
                    helperText={field.helpText || ''}
                    required={field.required}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const day = String(now.getDate()).padStart(2, '0');
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');
                      const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
                      setValue(datetimeLocal);
                    }}
                    disabled={isTicketInFinalStatus}
                    sx={{ 
                      minWidth: 'auto', 
                      px: 2, 
                      height: 56,
                      position: 'relative',
                      top: 8
                    }}
                  >
                    {t('now')}
                  </Button>
                </Box>
              );
            }
            if (field.type === 'MULTISELECT' || field.type === 'LIST') {
              const options = Array.isArray(field.options) ? field.options : [];
              const isMulti = field.type === 'MULTISELECT';
              const currentValue = isMulti
                ? (Array.isArray(value) ? value : [])
                : (value || null);
              const isValueEmpty = isMulti ? currentValue.length === 0 : !currentValue;
              
              return (
                <Autocomplete
                  key={field.id}
                  multiple={isMulti}
                  options={options}
                  value={currentValue}
                  onChange={(_, newValue) => {
                    setValue(isMulti ? newValue : (newValue || ''));
                    setCustomInputs(prev => ({ ...prev, [field.id]: '' }));
                  }}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  disableCloseOnSelect={isMulti}
                  filterSelectedOptions
                  noOptionsText="Нет вариантов"
                  disabled={isTicketInFinalStatus}
                  isOptionEqualToValue={(option, val) => option === val}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label={field.label}
                      fullWidth
                      helperText={field.helpText || ''}
                      required={field.required && isValueEmpty}
                      inputProps={{
                        ...params.inputProps,
                        // Блокируем нативную валидацию, если значение уже выбрано
                        required: field.required && isValueEmpty,
                      }}
                    />
                  )}
                />
              );
            }
            if (field.type === 'CHECKBOX') {
              return (
                <FormControl key={field.id} margin="dense">
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Tooltip title={field.helpText || ''}>
                      <span>
                        <IconButton size="small" disabled>
                          <></>
                        </IconButton>
                      </span>
                    </Tooltip>
                    <span style={{ marginRight: 8 }}>{field.label}</span>
                    <input type="checkbox" checked={!!value} onChange={(e) => setValue(e.target.checked)} disabled={isTicketInFinalStatus} />
                  </Box>
                </FormControl>
              );
            }
            return null;
          })}
          <TextField
            autoFocus
            margin="dense"
            label={t('title')}
            type="text"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            disabled={isTicketInFinalStatus}
          />
          <TextField
            margin="dense"
            label={t('description')}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            disabled={isTicketInFinalStatus}
          />
          {user?.role === 'admin' && (
            <Autocomplete
              fullWidth
              size="small"
              disabled={isTicketInFinalStatus}
              options={authors}
              filterOptions={(opts, state) => {
                if (!state.inputValue) return opts;
                if (selectedAuthor && state.inputValue === (selectedAuthor.name || '')) return opts;
                return defaultFilter(opts, state);
              }}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              value={selectedAuthor}
              onChange={(_, newValue) => {
                setFormData({ ...formData, createdById: newValue ? newValue.id : '' });
              }}
              renderOption={(props, author) => (
                <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontSize: '0.9rem', mr: 1 }}>
                    {(author.name || '').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 500 }}>{author.name}</Typography>
                    {author.organization && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {author.organization}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label="Автор"
                  placeholder="Выберите автора"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              noOptionsText="Нет вариантов"
            />
          )}
          <FormControl fullWidth margin="dense" disabled={isTicketInFinalStatus} variant="outlined">
            <InputLabel id="type-select-label" shrink={!!formData.type}>Тип заявки</InputLabel>
            <Select
              labelId="type-select-label"
              value={formData.type}
              label="Тип заявки"
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              MenuProps={{
                PaperProps: { style: { maxHeight: 300 } },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' }
              }}
            >
              <MenuItem value="bug">Бага</MenuItem>
              <MenuItem value="feature">Предложение</MenuItem>
              <MenuItem value="unknown">Неизвестно</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" disabled={(ticket && user?.role !== 'admin') || isTicketInFinalStatus} variant="outlined">
            <InputLabel id="priority-select-label" shrink={!!formData.priority}>Приоритет</InputLabel>
            <Select
              labelId="priority-select-label"
              value={formData.priority}
              label="Приоритет"
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              MenuProps={{ 
                PaperProps: { style: { maxHeight: 300 } },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' }
              }}
            >
              <MenuItem value="low">Низкий</MenuItem>
              <MenuItem value="normal">Средний</MenuItem>
              <MenuItem value="high">Высокий</MenuItem>
            </Select>
          </FormControl>
          {user?.role === 'admin' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth margin="dense"
                label="Плановая дата завершения"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.plannedEndDate}
                onChange={(e) => setFormData({ ...formData, plannedEndDate: e.target.value })}
                disabled={isTicketInFinalStatus}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const now = new Date();
                  const year = now.getFullYear();
                  const month = String(now.getMonth() + 1).padStart(2, '0');
                  const day = String(now.getDate()).padStart(2, '0');
                  const dateLocal = `${year}-${month}-${day}`;
                  setFormData({ ...formData, plannedEndDate: dateLocal });
                }}
                disabled={isTicketInFinalStatus}
                sx={{ 
                  minWidth: 'auto', 
                  px: 2, 
                  height: 56,
                  position: 'relative',
                  top: 8
                }}
              >
                {t('now')}
              </Button>
            </Box>
          )}
          
          {/* Выбор агента-исполнителя */}
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <AgentSelect
              value={formData.assignedToId}
              onChange={(val) => setFormData({ ...formData, assignedToId: val })}
              options={assignees}
            />
          )}
          {/* Поле выбора статуса - только для админов и менеджеров */}
          {formData.projectId && (user?.role === 'admin' || user?.role === 'manager') && (
            <FormControl fullWidth margin="dense" variant="outlined">
              <InputLabel id="status-select-label" shrink={!!formData.statusId}>Статус</InputLabel>
              <Select
                labelId="status-select-label"
                value={formData.statusId}
                label="Статус"
                onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                disabled={loadingStatuses || isTicketInFinalStatus}
                MenuProps={{ 
                  PaperProps: { style: { maxHeight: 300 } },
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' }
                }}
              >
                {loadingStatuses ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  projectStatuses.map(status => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box display="flex" alignItems="center">
                        <Box 
                          component="span" 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: status.color,
                            display: 'inline-block',
                            mr: 1
                          }} 
                        />
                        {status.name}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
          {/* Информация о статусе для обычных пользователей */}
          {formData.projectId && !(user?.role === 'admin' || user?.role === 'manager') && formData.statusId && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Статус:
              </Typography>
              {(() => {
                const status = projectStatuses.find(s => s.id === formData.statusId);
                return status ? (
                  <Box display="flex" alignItems="center">
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: status.color,
                        display: 'inline-block',
                        mr: 1
                      }} 
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {status.name}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Загружается...
                  </Typography>
                );
              })()}
            </Box>
          )}
          <FormControl fullWidth margin="dense">
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title={isTicketInFinalStatus ? 'Недоступно в финальном статусе' : (t('attachFile') || 'Прикрепить файлы')}>
                <span>
                  <IconButton color="primary" component="label" size="large" disabled={isTicketInFinalStatus}>
                    <AttachFileIcon />
                    <input 
                      type="file" 
                      hidden 
                      multiple 
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        setAttachments(prev => {
                          const combined = [...Array.from(prev || []), ...files];
                          // Убираем дубликаты по имени+размеру+mtime
                          const seen = new Set();
                          const unique = [];
                          for (const f of combined) {
                            const key = `${f.name}|${f.size}|${f.lastModified}`;
                            if (!seen.has(key)) {
                              seen.add(key);
                              unique.push(f);
                            }
                          }
                          return unique;
                        });
                        // Сброс, чтобы можно было выбрать те же файлы снова
                        e.target.value = null;
                      }} 
                      disabled={isTicketInFinalStatus} 
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <span style={{ color: '#757575', fontSize: '0.9rem', marginLeft: '8px' }}>
                {attachments.length > 0 ? `${attachments.length} ${t('files')}` : t('attachFile')}
              </span>
            </Box>
            {attachments.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {Array.from(attachments).map((file, idx) => (
                  <Chip 
                    key={`${file.name}|${file.size}|${file.lastModified}|${idx}`} 
                    label={file.name} 
                    onDelete={() => {
                      setAttachments(prev => Array.from(prev).filter((_, i) => i !== idx));
                    }}
                  />
                ))}
              </Stack>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          {!isTicketInFinalStatus && (
            <Button type="submit" variant="contained" color="primary">
              {ticket ? t('edit') : t('create')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TicketForm;
